import { cn } from '~/lib/utils';
import styles from './AnswerHeading.module.css';
import type { ChatTheme } from './theme';
import { chatThemeClass } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

export interface AnswerHeadingProps {
  /** Plain text. A heading with markup in it is a paragraph pretending to be a heading. */
  children: string;
  theme?: ChatTheme;
  className?: string;
}

/**
 * A heading inside a structured assistant answer (*Rich answer* — "a heading, a table and numbered
 * steps. prose was only ever paragraphs.").
 *
 * Same 14/22 −0.01em metrics as `AssistantProse`; only weight (600) and colour (`--cv15-text`)
 * separate them, so a heading never breaks the column's rhythm.
 *
 * Rendered as an `h3` rather than a styled `div`: the thread is the page's landmark and a message
 * is a section within it, which puts a heading *inside* a message at the third level. The level is
 * fixed rather than a prop because a thread whose heading levels varied per message would give
 * screen-reader users a broken outline, which is worse than one that is uniformly one level too
 * deep.
 */
export function AnswerHeading({ children, theme: themeProp, className }: AnswerHeadingProps) {
  const theme = useV15Theme(themeProp);
  return <h3 className={cn(chatThemeClass(theme), styles.root, className)}>{children}</h3>;
}
